import { Component, OnDestroy, OnInit } from '@angular/core';
import { TableType } from '../../models/table-type';
import { SqlRecordOcclusionType } from '../../models/sql-record-occlusion-type';
import { SqlColumnDefinition } from '../../models/sql-column-definition';
import { SqlColumnTemplate } from '../../models/sql-column-template';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
    selector: 'app-model-builder',
    templateUrl: './model-builder.component.html',
    styleUrl: './model-builder.component.css'
})

export class ModelBuilderComponent implements OnInit, OnDestroy {

    public sqlTableTypes: TableType[] = [
        { TableTypeId: 2, Name: "Transactional" },
        { TableTypeId: 1, Name: "Fact or Enum" }
    ];

    public sqlRecordOcclusionTypes: SqlRecordOcclusionType[] = [
        { SqlRecordOcclusionTypeId: 0, Name: "(None)" },
        { SqlRecordOcclusionTypeId: 1, Name: "SoftDeletable" },
        { SqlRecordOcclusionTypeId: 2, Name: "Archivable" },
        { SqlRecordOcclusionTypeId: 3, Name: "Both" }
    ];

    public sqlColumnTemplates: SqlColumnTemplate[] = [
        {
            Name: "Name",
            Description: "Basic non-nullable object name",
            Definition: Object.assign(new SqlColumnDefinition(), {
                Name: "Name",
                DataTypeNameWithSize: "VARCHAR(100)",
                IsNullable: false,
                IsUnique: false,
                DefaultValue: null,
                ForeignKeyFullColumnName: ""
            })
        },
        {
            Name: "Code",
            Description: "Unique non-nullable, alpha-numeric code",
            Definition: Object.assign(new SqlColumnDefinition(), {
                Name: "Code",
                DataTypeNameWithSize: "VARCHAR(100)",
                IsNullable: false,
                IsUnique: true,
                DefaultValue: null,
                ForeignKeyFullColumnName: ""
            })
        }
    ];

    public selectedTemplate: SqlColumnTemplate | null = null;

    public columnDefinitions: SqlColumnDefinition[] = [];

    // state
    public selectedTableType: TableType = this.sqlTableTypes[0];
    public selectedOcclusionType: SqlRecordOcclusionType = this.sqlRecordOcclusionTypes[0];
    public schemaName: string = "";
    public tableName: string = "";
    public tableCreateScript: string = "";
    public csharpModel: string = "";
    public projectName: string = "";

    // regen trigger
    private regen$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    public ngOnInit(): void {

        // Debounce so we don't regenerate on every single keystroke instantly
        this.regen$.pipe(debounceTime(150), takeUntil(this.destroy$)).subscribe(() =>
            this.generateScripts()
        );

        // initial render
        this.touch();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Trigger a debounced regeneration of the script */
    public touch(): void {
        this.regen$.next();
    }

    public addColumn = (): void => {
        this.columnDefinitions.push(new SqlColumnDefinition());
        this.touch();
    };

    public removeColumn = (columnDefinition: SqlColumnDefinition): void => {
        const index = this.columnDefinitions.indexOf(columnDefinition);
        if (index >= 0) {
            this.columnDefinitions.splice(index, 1);
            this.touch();
        }
    };

    public generateScripts = (): void => {

        this.generateSqlTableScript();
        this.generateCsharpModel();
    }

    public generateSqlTableScript = (): void => {

        // handle empty schema/table cases (unchanged)
        if (!this.schemaName && !this.tableName) {
            this.tableCreateScript = '(Enter schema and table name)';
            return;
        }
        if (!this.schemaName) { this.tableCreateScript = '(Enter schema name)'; return; }
        if (!this.tableName)  { this.tableCreateScript = '(Enter table name)';  return; }

        const S = this.id(this.schemaName);
        const T = this.id(this.tableName);

        this.tableCreateScript = "-- generated by the remitter.supermodel.builder";
        this.tableCreateScript += `\r\nCREATE TABLE ${S}.${T} (\r\n`;

        // columns
        this.tableCreateScript += `\t${this.id(`${this.tableName}Id`)} INT IDENTITY(1, 1) NOT NULL`;

        // dynamic columns
        for (const col of this.columnDefinitions) {
            if (col?.Name) {
                // ensure uppercased type in script too
                const colType = (col.DataTypeNameWithSize || '').toUpperCase();
                const nullability = col.IsNullable ? 'NULL' : 'NOT NULL';

                this.tableCreateScript += `,\r\n\t${this.id(col.Name)} ${colType} ${nullability}`;

                if (col.DefaultValue && col.DefaultValue.trim().length > 0) {
                    const defName = `DF_${this.schemaName}_${this.tableName}_${col.Name}`;
                    const defVal = this.formatDefault(col.DefaultValue);
                    this.tableCreateScript += ` CONSTRAINT ${this.id(defName)} DEFAULT ${defVal}`;
                }
            }
        }

        // standard columns (now bracketed)
        this.tableCreateScript += `,\r\n\t${this.id('CreatedOnUtc')} DATETIME NOT NULL CONSTRAINT ${this.id(`DF_${this.schemaName}_${this.tableName}_CreatedOnUtc`)} DEFAULT GETUTCDATE()`;
        this.tableCreateScript += `,\r\n\t${this.id('CreatedBy')} VARCHAR(100) NOT NULL`;

        if (this.selectedTableType?.TableTypeId === 2) { // transactional
            this.tableCreateScript += `,\r\n\t${this.id('UpdatedOnUtc')} DATETIME NULL`;
            this.tableCreateScript += `,\r\n\t${this.id('UpdatedBy')} VARCHAR(100) NULL`;
        }

        if (this.selectedOcclusionType.SqlRecordOcclusionTypeId === 1 || this.selectedOcclusionType.SqlRecordOcclusionTypeId === 3) {
            this.tableCreateScript += `,\r\n\t${this.id('DeletedOnUtc')} DATETIME NULL`;
            this.tableCreateScript += `,\r\n\t${this.id('DeletedBy')} VARCHAR(100) NULL`;
        }

        if (this.selectedOcclusionType.SqlRecordOcclusionTypeId === 2 || this.selectedOcclusionType.SqlRecordOcclusionTypeId === 3) {
            this.tableCreateScript += `,\r\n\t${this.id('ArchivedOnUtc')} DATETIME NULL`;
            this.tableCreateScript += `,\r\n\t${this.id('ArchivedBy')} VARCHAR(100) NULL`;
        }

        // primary key (bracket constraint name, table/col identifiers)
        const pkName = `PK_${this.schemaName}_${this.tableName}`;
        this.tableCreateScript += `,\r\n\tCONSTRAINT ${this.id(pkName)} PRIMARY KEY CLUSTERED ${S}.${T}(${this.id(`${this.tableName}Id`)} ASC)`;

        // foreign keys (now bracket names and referenced identifiers)
        for (const col of this.columnDefinitions) {
            if (col.ForeignKeyFullColumnName && col.ForeignKeyFullColumnName.trim().length > 0) {
                const parts = col.ForeignKeyFullColumnName.split('.').map(p => p.trim());
                if (parts.length === 3) {
                    const [refSchema, refTable, refColumn] = parts;
                    const fkName = `FK_${this.schemaName}_${this.tableName}_${col.Name}`;
                    this.tableCreateScript +=
                        `,\r\n\tCONSTRAINT ${this.id(fkName)} FOREIGN KEY (${this.id(col.Name)}) REFERENCES ${this.id(refSchema)}.${this.id(refTable)}(${this.id(refColumn)})`;
                }
            }
        }

        // UNIQUE constraints (already bracketing names/cols via this.id)
        for (const col of this.columnDefinitions) {
            if (col?.Name && col.IsUnique) {
                const uqName = `UQ_${this.schemaName}_${this.tableName}_${col.Name}`;
                this.tableCreateScript +=
                    `,\r\n\tCONSTRAINT ${this.id(uqName)} UNIQUE NONCLUSTERED (${this.id(col.Name)} ASC)`;
            }
        }

        this.tableCreateScript += `\r\n)`;
    };

    public generateCsharpModel = (): void => {

        const missingSchema  = this.schemaName.trim() === "";
        const missingTable   = this.tableName.trim() === "";
        const missingProject = this.projectName.trim() === "";

        // Compose the guidance message dynamically (no early returns)
        {
            const missingParts: string[] = [];
            if (missingTable)   missingParts.push("table");
            if (missingSchema)  missingParts.push("schema");
            if (missingProject) missingParts.push(".NET project name");

            if (missingParts.length > 0) {
                const joinHuman = (items: string[]): string => {
                    if (items.length === 1) return items[0];
                    if (items.length === 2) return `${items[0]} and ${items[1]}`;
                    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
                };
                this.csharpModel = `(Enter ${joinHuman(missingParts)})`;
            } else {
                // All required identifiers are present — build the class
                type Col = { name: string; sqlType: string; nullable: boolean };
                const cols: Col[] = [];

                // PK first
                const pkName = `${this.tableName}Id`;
                cols.push({ name: pkName, sqlType: "INT", nullable: false });

                // Dynamic columns from grid
                for (const col of this.columnDefinitions) {
                    if (col?.Name && col.DataTypeNameWithSize) {
                        cols.push({
                            name: col.Name,
                            sqlType: col.DataTypeNameWithSize,
                            nullable: !!col.IsNullable
                        });
                    }
                }

                // Standard columns
                cols.push({ name: "CreatedOnUtc", sqlType: "DATETIME",     nullable: false });
                cols.push({ name: "CreatedBy",    sqlType: "VARCHAR(100)", nullable: false });

                if (this.selectedTableType?.TableTypeId === 2) { // Transactional
                    cols.push({ name: "UpdatedOnUtc", sqlType: "DATETIME",     nullable: true });
                    cols.push({ name: "UpdatedBy",    sqlType: "VARCHAR(100)", nullable: true });
                }

                const occ = this.selectedOcclusionType?.SqlRecordOcclusionTypeId ?? 0;
                if (occ === 1 || occ === 3) {
                    cols.push({ name: "DeletedOnUtc", sqlType: "DATETIME",     nullable: true });
                    cols.push({ name: "DeletedBy",    sqlType: "VARCHAR(100)", nullable: true });
                }
                if (occ === 2 || occ === 3) {
                    cols.push({ name: "ArchivedOnUtc", sqlType: "DATETIME",     nullable: true });
                    cols.push({ name: "ArchivedBy",    sqlType: "VARCHAR(100)", nullable: true });
                }

                // Build the class text with Dapper details
                this.csharpModel  = "// generated by the remitter.supermodel.builder";
                this.csharpModel += "\r\n// be sure to place this file in a folder who\'s path matches the namespace";
                this.csharpModel += "\r\nusing Dapper.Contrib.Extensions;";
                this.csharpModel += "\r\n";
                this.csharpModel += "\r\nnamespace " + this.projectName + ".Models.SQL." + this.schemaName;
                this.csharpModel += "\r\n{";
                this.csharpModel += `\r\n\t[Table("${this.schemaName}.${this.tableName}")]`;
                this.csharpModel += "\r\n\tpublic class " + this.tableName;
                this.csharpModel += "\r\n\t{";

                for (const c of cols) {
                    const csType = this.mapSqlToCSharp(this.baseSqlType(c.sqlType), c.nullable);
                    const prop   = this.csharpIdentifier(c.name);

                    // Add [Key] on the PK property
                    if (prop === this.csharpIdentifier(pkName)) {
                        this.csharpModel += `\r\n\t\t[Key]`;
                    }

                    this.csharpModel += `\r\n\t\tpublic ${csType} ${prop} { get; set; }`;
                }

                this.csharpModel += "\r\n\t}";
                this.csharpModel += "\r\n}";
            }
        }
    };

    /** Normalize an incoming SQL type like "VARCHAR(100)" → "VARCHAR" */
    private baseSqlType = (sqlType: string): string => {
        const t = (sqlType || "").trim().toUpperCase();
        // strip size/precision e.g., VARCHAR(100), DECIMAL(18,2)
        return t.replace(/\s+/g, " ").replace(/\(.*\)/, "").trim();
    };

    /** Map SQL type → C# type, honoring nullability. */
    private mapSqlToCSharp = (baseType: string, nullable: boolean): string => {
        // value vs reference type handling
        const ref = (name: string) => nullable ? `${name}?` : name;

        switch (baseType) {
            // integers
            case "BIGINT": return nullable ? "long?"  : "long";
            case "INT":    return nullable ? "int?"   : "int";
            case "SMALLINT": return nullable ? "short?" : "short";
            case "TINYINT":  return nullable ? "byte?"  : "byte";

            // numeric
            case "DECIMAL":
            case "NUMERIC":
            case "MONEY":
            case "SMALLMONEY":
                return nullable ? "decimal?" : "decimal";
            case "FLOAT":  return nullable ? "double?" : "double";
            case "REAL":   return nullable ? "float?"  : "float";

            // text
            case "VARCHAR":
            case "NVARCHAR":
            case "CHAR":
            case "NCHAR":
            case "TEXT":
            case "NTEXT":
            case "XML":
            case "JSON":
                return nullable ? "string?" : "string";

            // uniqueidentifier
            case "UNIQUEIDENTIFIER":
                return nullable ? "Guid?" : "Guid";

            // dates/times
            case "DATETIME":
            case "DATETIME2":
            case "SMALLDATETIME":
            case "DATE":
            case "TIME":
                return nullable ? "DateTime?" : "DateTime";

            // binary
            case "VARBINARY":
            case "BINARY":
            case "IMAGE":
                // arrays are reference types; allow nullability annotation if you use C# nullable context
                return nullable ? "byte[]?" : "byte[]";

            // boolean
            case "BIT":
                return nullable ? "bool?" : "bool";

            // spatial/others → treat as string by default
            case "GEOGRAPHY":
            case "GEOMETRY":
            default:
                return nullable ? "string?" : "string";
        }
    };

    /** Basic cleanup so property names are valid-ish C# identifiers. */
    private csharpIdentifier = (name: string): string => {
        // strip SQL brackets and trim
        let n = (name || "").replace(/\[|\]/g, "").trim();

        // if name starts with a digit or is empty, prefix with '_'
        if (!n || /^\d/.test(n)) n = "_" + n;

        // replace spaces and invalid chars with underscores
        n = n.replace(/[^\w]/g, "_");

        // PascalCase-ish: split on underscores/spaces and capitalize
        n = n.split(/[_\s]+/).map(part => part ? part[0].toUpperCase() + part.slice(1) : "").join("");

        return n;
    };

    /** Returns true if the datatype typically takes a size (e.g., VARCHAR, NVARCHAR, CHAR, VARBINARY). */
    private needsSize = (type: string | null | undefined): boolean => {
        if (!type) return false;
        const t = type.trim().toUpperCase();
        return /(VAR)?CHAR|N(VAR)?CHAR|BINARY|VARBINARY/.test(t);
    };

    /** True if the default looks like a SQL function/literal that shouldn't be quoted. */
    private isSqlFunctionLiteral = (v: string): boolean => {
        const up = v.trim().toUpperCase();
        return /^(GETDATE\(\)|GETUTCDATE\(\)|NEWID\(\)|CURRENT_TIMESTAMP|NULL|TRUE|FALSE|\d+(\.\d+)?|\(.*\))$/.test(up);
    };

    /** Formats default value as a SQL literal (quotes strings unless it matches known function/literal). */
    private formatDefault = (v: string): string => {
        const trimmed = v.trim();
        if (this.isSqlFunctionLiteral(trimmed)) return trimmed;
        return `'${trimmed.replace(/'/g, "''")}'`;
    };

    /** Bracket-quote an identifier (simple & safe). */
    private id = (name: string): string => `[${name.replace(/]/g, ']]')}]`;

    public addTemplateColumn = (): void => {
      if (!this.selectedTemplate) return;

      // clone the template definition into a fresh SqlColumnDefinition instance
      const def = Object.assign(new SqlColumnDefinition(), this.selectedTemplate.Definition);

      this.columnDefinitions.push(def);

      // clear selection and trigger regeneration (debounced by touch)
      this.selectedTemplate = null;
      this.touch();
    };

    public onTypeInput(col: SqlColumnDefinition, value: string): void {
      col.DataTypeNameWithSize = (value || "").toUpperCase();
      this.touch();
    }

    public copyToClipboard(value: string): void {

        if (!value) {
            return;
        }

        navigator.clipboard.writeText(value).then(() => {
            // Optional: show success message using PrimeNG toast or alert
            console.log('Copied to clipboard');
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    }
}
